/**
 * IntakeForm — shown when a visitor lands on a domain that resolves to no
 * brokerage. Rather than dead-end them (or invent a tenant named after them,
 * the Checkpoint-#14 defect), we take their details and email the support team.
 * No account is created.
 */

import { Building2, CheckCircle2, Loader2 } from "lucide-react";
import { useState } from "react";

import { PhoneInput } from "@re-platform/ui";

import { identityApi } from "./passkeyApi";
import "./SignIn.css";

type Status = "idle" | "sending" | "sent" | "error";

export function IntakeForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await identityApi.submitIntake({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        message: message.trim() || undefined,
      });
      setStatus("sent");
    } catch (err: unknown) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  if (status === "sent") {
    return (
      <div
        className="signin__brand"
        style={{ flexDirection: "column", gap: 12 }}
      >
        <CheckCircle2 size={28} aria-hidden />
        <h1>Thanks — we've got your details</h1>
        <p className="signin__subtitle">Someone will be in touch shortly.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => void onSubmit(e)}
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div className="signin__brand">
        <Building2 size={28} aria-hidden />
        <span>Get in touch</span>
      </div>
      <p className="signin__subtitle">
        We couldn't match this page to a brokerage. Leave your details and a
        licensed agent will reach out.
      </p>
      <input
        className="rp-input"
        placeholder="Your name"
        value={name}
        required
        onChange={(e) => setName(e.target.value)}
      />
      <input
        className="rp-input"
        type="email"
        placeholder="Email"
        value={email}
        required
        onChange={(e) => setEmail(e.target.value)}
      />
      <PhoneInput
        className="rp-input"
        placeholder="Phone (optional)"
        value={phone}
        onChange={(e164) => setPhone(e164 ?? "")}
      />
      <textarea
        className="rp-input"
        placeholder="How can we help? (optional)"
        rows={3}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />
      {status === "error" && (
        <div className="signin__error" role="alert">
          <p>{error}</p>
        </div>
      )}
      <button
        type="submit"
        className="signin__cta rp-btn rp-btn--primary"
        disabled={status === "sending"}
      >
        {status === "sending" ? (
          <Loader2 className="signin__spin" size={18} aria-hidden />
        ) : null}
        Send
      </button>
    </form>
  );
}
